<template>
    <div class="rich-text-editor">
        <div :id="editorId">
            <svg-select-all v-show="false" ref="svgAll"></svg-select-all>
            <paste-format v-show="false" ref="pasteFormatIcon"></paste-format>
            <image-link v-show="false" ref="imageLinkIcon"></image-link>
            <image-size v-show="false" ref="imageSizeIcon"></image-size>
        </div>
    </div>
</template>

<script>
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import ImageLink from '@/components/svg/ImageLink.vue';
import ImageSize from '@/components/svg/ImageSize.vue';
import PasteFormat from '@/components/svg/PasteFormat.vue';
import SelectAll from '@/components/svg/SelectAll.vue';

let uidCounter = 0;

const COLORS = [
    '#000000', '#e60000', '#ff9900', '#ffff00', '#008A00', '#0066cc', '#9933ff',
    '#ffffff', '#facccc', '#ffebcc', '#ffffcc', '#cce8cc', '#cce0f5', '#ebd6ff',
    '#bbbbbb', '#f06666', '#ffc266', '#ffff66', '#66b966', '#66a3e0', '#c285ff',
    '#888888', '#a10000', '#b26b00', '#b2b200', '#006100', '#2F5496', '#6b24b2',
    '#444444', '#5c0000', '#663d00', '#666600', '#003700', '#002966', '#3d1466',
];

export default {
    name: 'rich-text-editor',
    props: {
        modelValue: {
            type: String,
            default: '',
        },
    },
    components: {
        'svg-select-all': SelectAll,
        'paste-format': PasteFormat,
        'image-link': ImageLink,
        'image-size': ImageSize,
    },
    emits: ['update:modelValue'],
    data() {
        uidCounter += 1;
        return {
            editorId: `rte-${uidCounter}`,
            quill: null,
            pasteFormat: false,
            selectedImage: null,
        };
    },
    watch: {
        modelValue(newValue) {
            if (!this.quill) return;
            if (newValue === this.quill.root.innerHTML) return;
            this.quill.root.innerHTML = newValue || '';
        },
    },
    mounted() {
        const Size = Quill.import('attributors/style/size');
        Size.whitelist = ['12px', '14px', '16px', '18px'];
        Quill.register(Size, true);

        const ImageWidth = Quill.import('attributors/style/size');
        ImageWidth.whitelist = ['25px', '50px', '75px', '100px', '150px', '200px', '250px', '300px', '400px', '500px', '750px', '1000px'];
        Quill.register(ImageWidth, true);

        const self = this;
        const toolbarOptions = {
            container: [
                { size: ['12px', '14px', '16px', '18px'] },
                'bold', 'italic', 'underline', 'clean',
                'select-all', 'paste-format',
                { list: 'ordered' }, { list: 'bullet' },
                { color: COLORS }, { background: COLORS },
                { align: ['', 'center', 'right', 'justify'] },
                'link', 'image', 'image-link',
                { size2: ['25px', '50px', '75px', '100px', '150px', '200px', '250px', '300px', '400px', '500px', '750px', '1000px'] },
            ],
            handlers: {
                'select-all': () => { self.quill.setSelection(0, self.quill.getLength()); },
                'paste-format': () => { self.pasteFormat = !self.pasteFormat; },
                'image-link': () => {
                    const url = prompt('Enter the image URL:');
                    if (url) {
                        const range = self.quill.getSelection(true);
                        self.quill.insertEmbed(range.index, 'image', url);
                    }
                },
                size2: (value) => {
                    if (self.selectedImage) {
                        self.selectedImage.style.width = value;
                        self.selectedImage.setAttribute('width', value);
                        self.emitUpdate();
                        return;
                    }
                    const selection = self.quill.getSelection();
                    if (!selection) return;
                    self.applyImageWidth(selection, value);
                },
            },
        };

        this.quill = new Quill(`#${this.editorId}`, {
            modules: { toolbar: toolbarOptions },
            theme: 'snow',
        });
        this.quill.root.innerHTML = this.modelValue || '';

        this.quill.on('text-change', (delta, oldDelta, source) => {
            if (source === 'user') this.emitUpdate();
        });

        this.quill.root.addEventListener('dblclick', (e) => {
            if (e.target.tagName === 'IMG') {
                const imgIndex = this.getImageIndex(e.target);
                this.quill.setSelection(imgIndex, 1);
                this.setSelectedImage(e.target);
            }
        });

        this.quill.root.addEventListener('click', (e) => {
            if (e.target.tagName === 'IMG') {
                const imgIndex = this.getImageIndex(e.target);
                this.quill.setSelection(imgIndex, 1);
                this.setSelectedImage(e.target);
            } else {
                this.setSelectedImage(null);
            }
        });

        this.quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
            if (!self.pasteFormat) {
                delta.ops = delta.ops.map((op) => ({ insert: op.insert }));
            }
            return delta;
        });

        this.injectToolbarIcons();
    },
    beforeUnmount() {
        this.setSelectedImage(null);
    },
    methods: {
        emitUpdate() {
            this.$emit('update:modelValue', this.quill.root.innerHTML);
        },
        injectToolbarIcons() {
            const toolbar = this.$el.querySelector('.ql-toolbar');
            if (!toolbar) return;
            const mount = (refName, buttonClass) => {
                const icon = this.$refs[refName];
                const button = toolbar.querySelector(`.${buttonClass}`);
                if (icon && button) button.innerHTML = icon.$el.innerHTML;
            };
            mount('svgAll', 'ql-select-all');
            mount('pasteFormatIcon', 'ql-paste-format');
            mount('imageLinkIcon', 'ql-image-link');
            mount('imageSizeIcon', 'ql-size2');
        },
        applyImageWidth(selection, widthValue) {
            if (selection.length > 0) {
                const delta = this.quill.getContents(selection.index, selection.length);
                const hasImages = delta.ops.some((op) => op.insert && typeof op.insert === 'object' && op.insert.image);
                if (hasImages) {
                    this.quill.root.querySelectorAll('img').forEach((img) => {
                        const imgIndex = this.getImageIndex(img);
                        if (imgIndex >= selection.index && imgIndex < selection.index + selection.length) {
                            img.style.width = widthValue;
                            img.setAttribute('width', widthValue);
                        }
                    });
                    this.emitUpdate();
                    return;
                }
            }
            let closestImage = null;
            let closestDistance = Infinity;
            this.quill.root.querySelectorAll('img').forEach((img) => {
                const imgIndex = this.getImageIndex(img);
                const distance = Math.abs(imgIndex - selection.index);
                if (distance < closestDistance) {
                    closestDistance = distance;
                    closestImage = img;
                }
            });
            if (closestImage) {
                closestImage.style.width = widthValue;
                closestImage.setAttribute('width', widthValue);
                this.emitUpdate();
            }
        },
        setSelectedImage(imgElement) {
            if (this.quill && this.quill.root) {
                this.quill.root.querySelectorAll('img').forEach((img) => img.removeAttribute('data-quill-selected'));
            }
            this.selectedImage = imgElement;
            if (this.selectedImage) this.selectedImage.setAttribute('data-quill-selected', 'true');
        },
        getImageIndex(imgElement) {
            const walker = document.createTreeWalker(this.quill.root, NodeFilter.SHOW_ALL, null, false);
            let index = 0;
            let node;
            while ((node = walker.nextNode())) {
                if (node === imgElement) return index;
                if (node.nodeType === Node.TEXT_NODE) index += node.textContent.length;
                else if (node.tagName === 'IMG' || node.tagName === 'BR') index += 1;
            }
            return index;
        },
    },
};
</script>

<style scoped>
.rich-text-editor :deep(.ql-container) {
    min-height: 260px;
    font-size: 1rem;
}
</style>

<style>
img[data-quill-selected="true"] {
    border: 2px solid #007bff !important;
    box-shadow: 0 0 5px rgba(0, 123, 255, 0.5) !important;
}
.ql-editor img {
    border: none;
    box-shadow: none;
}
</style>
